import { z } from 'zod/v3';

/**
 * 배송지 수령 위치
 */
export const deliveryLocationSchema = z.enum([
  'NONE',
  'INSIDE_STORE',
  'REFRIGERATOR',
  'CUSTOM',
]);
export type DeliveryLocation = z.infer<typeof deliveryLocationSchema>;

/**
 * 마켓 배송지 정보 스키마
 * @see GET /api/market-place/v1/shipping-address
 * @see shoebill MarketShippingAddressResponse
 */
export const marketShippingAddressSchema = z.object({
  /** 배송지 주소 ID */
  id: z.number().optional(),
  /** 구매자 ID */
  buyerId: z.number().optional(),
  /** 상호명 */
  businessName: z.string().optional(),
  /** 수령자 */
  recipient: z.string().optional(),
  /** 수령자 전화번호 */
  recipientPhoneNumber: z.string().optional(),
  /** 우편번호(zonecode) */
  zipCode: z.string().optional(),
  /** 주소지 */
  address: z.string().optional(),
  /** 상세주소지 */
  detailAddress: z.string().optional(),
  /** 보여지는 주소지(특별시/광역시 단위 지역 제외) */
  displayAddress: z.string().optional(),
  /** 특별시/광역시 단위 지역 이름(sido) */
  provincialLevelDivisionName: z.string().optional(),
  /** 법정동 코드 */
  regionCode: z.string().optional(),
  /** 주소지 존재 여부 */
  hasAddress: z.boolean().optional(),
  /** 기본 배송지 여부 */
  isDefault: z.boolean().optional(),
  /** 사업장 주소지 여부 */
  isBusinessAddress: z.boolean().optional(),
  /** 사업장 주소지 여부 (legacy 필드) */
  businessAddress: z.boolean().optional(),
  /** 기본 여부 (legacy) */
  default: z.boolean().optional(),
  /** 선택된 수령 위치 */
  deliveryLocation: deliveryLocationSchema.optional(),
  /** 업데이트 전 배송 요청사항 */
  originRequestForDelivery: z.string().optional(),
  /** 선택된 배송 요청사항 */
  requestForDelivery: z.string().optional(),
  /** 생성일 */
  createdAt: z.string().optional(),
  /** 수정일 */
  updatedAt: z.string().optional(),
  /** 수령 위치 정보 목록 */
  locations: z.array(z.unknown()).optional(),
});
export type MarketShippingAddress = z.infer<typeof marketShippingAddressSchema>;

/**
 * 배송지 등록 요청 스키마
 * @see POST /api/market-place/v2/shipping-address
 * @see shoebill CreateShippingAddressRequest
 */
export const createShippingAddressRequestSchema = z.object({
  /** 주소지 */
  address: z.string(),
  /** 사업장 주소 여부 */
  businessAddress: z.boolean().optional(),
  /** 상호명 */
  businessName: z.string().optional(),
  /** 배송지 수령 위치 */
  deliveryLocation: deliveryLocationSchema.optional(),
  /** 상세주소지 */
  detailAddress: z.string().optional(),
  /** 약관 동의 상세 UUID 리스트 */
  items: z.array(z.string()).optional(),
  /** 특별시/광역시 단위 지역 이름(sido) */
  provincialLevelDivisionName: z.string(),
  /** 수령자 */
  recipient: z.string(),
  /** 수령자 전화번호 */
  recipientPhoneNumber: z.string(),
  /** 법정동 코드(bcode) */
  regionCode: z.string().optional(),
  /** 배송 후 요청사항 */
  requestForDelivery: z.string().optional(),
  /** 약관 응답 UUID */
  termsId: z.string().optional(),
  /** 우편번호(zonecode) */
  zipCode: z.string(),
});
export type CreateShippingAddressRequest = z.infer<
  typeof createShippingAddressRequestSchema
>;

/**
 * 배송지 변경 요청 스키마
 * @see PUT /api/market-place/v1/shipping-address
 * @see shoebill UpdateShippingAddressRequest
 */
export const updateShippingAddressRequestSchema = z.object({
  /** 배송지 ID */
  shippingAddressId: z.number(),
  /** 주소지 */
  address: z.string(),
  /** 상호명 */
  businessName: z.string().optional(),
  /** 수령 위치 */
  deliveryLocation: deliveryLocationSchema.optional(),
  /** 상세주소지 */
  detailAddress: z.string().optional(),
  /** 수령자 */
  recipient: z.string(),
  /** 수령자 전화번호 */
  recipientPhoneNumber: z.string(),
  /** 법정동 코드(bcode) */
  regionCode: z.string(),
  /** 배송 요청 사항 */
  requestForDelivery: z.string().optional(),
  /** 우편번호(zonecode) */
  zipCode: z.string(),
});
export type UpdateShippingAddressRequest = z.infer<
  typeof updateShippingAddressRequestSchema
>;

/**
 * 배송지 삭제 요청 스키마
 * @see DELETE /api/market-place/v1/shipping-address
 */
export const deleteShippingAddressRequestSchema = z.object({
  /** 배송지 ID */
  shippingAddressId: z.number(),
});
export type DeleteShippingAddressRequest = z.infer<
  typeof deleteShippingAddressRequestSchema
>;

/**
 * 기본 배송지 변경 요청 스키마
 * @see PATCH /api/market-place/v1/shipping-address/default-change
 */
export const updateDefaultShippingAddressRequestSchema = z.object({
  /** 배송지 ID */
  shippingAddressId: z.number(),
});
export type UpdateDefaultShippingAddressRequest = z.infer<
  typeof updateDefaultShippingAddressRequestSchema
>;
